import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { SERVER_URL } from './config';
import Navigation from './components/Navigation';
import PayPalConnectAccount from './components/PayPalConnectAccount';
import { getPayPalOnboardingContext, clearPayPalOnboardingContext } from './utils/paypalOnboardingHelper';

function UserSettings() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    show_gift_stats: true,
    show_gift_amounts: true,
    default_visibility: 'everyone',
  });

  useEffect(() => {
    // Wait for auth check to complete before redirecting
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Fetch current user settings
    fetch(`${SERVER_URL}/users/${user.id}`)
      .then(res => res.json())
      .then(data => {
        setSettings({
          show_gift_stats: data.show_gift_stats ?? true,
          show_gift_amounts: data.show_gift_amounts ?? true,
          default_visibility: data.default_visibility || 'everyone',
        });
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading settings:', err);
        setMessage({ type: 'error', text: 'Failed to load settings' });
        setLoading(false);
      });
  }, [authLoading, isAuthenticated, user, navigate]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${SERVER_URL}/users/${user.id}/privacy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      setMessage({ type: 'success', text: 'Settings saved successfully!' });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setMessage({
        type: 'error',
        text: 'Failed to save settings. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-2 text-gray-600">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-indigo-600 hover:text-indigo-700 mb-4 flex items-center gap-2"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your privacy and preferences</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Settings Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-8">
          {/* Payment Settings Section */}
          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Settings</h2>
            <p className="text-sm text-gray-600 mb-6">
              Manage your payment account. Gifts will be securely transferred to your connected
              account.
            </p>

            {/* <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Stripe Dashboard</div>
                  <div className="text-sm text-gray-600 mt-1">
                    View your balance, transaction history, and manage payout settings
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(
                        `${SERVER_URL}/users/${user.id}/dashboard-link-stripe`,
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                        }
                      );
                      const data = await response.json();
                      if (data.url) {
                        window.open(data.url, '_blank');
                      }
                    } catch (err) {
                      console.error('Error opening Stripe dashboard:', err);
                      setMessage({
                        type: 'error',
                        text: 'Failed to open Stripe dashboard',
                      });
                    }
                  }}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Open Dashboard →
                </button>
              </div>
            </div> */}

            {/* PayPal Account Connection (Commerce Platform - Recommended) */}
            <div className="mt-6">
              {/* <div className="mb-4">
                <div className="font-medium text-gray-900 mb-1">PayPal Account</div>
                <div className="text-sm text-gray-600">
                  Connect your PayPal account to receive gifts securely.
                </div>
              </div> */}
              <PayPalConnectAccount
                onSuccess={(merchantId) => {
                  refreshUser();
                  const onboardingContext = getPayPalOnboardingContext();
                  if (onboardingContext?.context === 'pool_acceptance' && onboardingContext?.data?.token) {
                    clearPayPalOnboardingContext();
                    navigate(`/accept-pool/${onboardingContext.data.token}`);
                  } else {
                    setMessage({
                      type: 'success',
                      text: 'PayPal account connected successfully!',
                    });
                  }
                }}
              />
            </div>
          </div>

          {/* Privacy Settings Section */}
          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Privacy Settings</h2>
            <p className="text-sm text-gray-600 mb-6">
              Control what information is visible on your profile
            </p>

            <div className="space-y-4">
              {/* Show Gift Stats */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="font-medium text-gray-900 block mb-1">
                    Show Gift Statistics
                  </label>
                  <p className="text-sm text-gray-600">
                    Display total given/received amounts and gift counts on your profile
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={settings.show_gift_stats}
                    onChange={e =>
                      setSettings({
                        ...settings,
                        show_gift_stats: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                </label>
              </div>

              {/* Show Gift Amounts */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="font-medium text-gray-900 block mb-1">
                    Show Dollar Amounts
                  </label>
                  <p className="text-sm text-gray-600">
                    Display specific dollar amounts in your activity feed
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={settings.show_gift_amounts}
                    onChange={e =>
                      setSettings({
                        ...settings,
                        show_gift_amounts: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                </label>
              </div>

              {/* Default Post Visibility */}
              {/* <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="font-medium text-gray-900 block mb-1">
                    Default Post Visibility
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    Choose who can see your posts by default when you create them
                  </p>
                  <select
                    value={settings.default_visibility}
                    onChange={(e) => setSettings({ ...settings, default_visibility: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="network">Network Only - Only people you follow and who follow you</option>
                    <option value="extended">Extended Network - Your network plus their connections</option>
                    <option value="everyone">Everyone - Public to all users</option>
                  </select>
                </div>
              </div> */}
            </div>
          </div>

          {/* Save Button */}
          <div className="border-t border-gray-200 pt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`w-full py-3 px-6 rounded-lg font-medium transition-all ${
                saving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-500 text-white hover:bg-indigo-600 shadow-md hover:shadow-lg'
              }`}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserSettings;
